import { useEffect, useState } from 'react'
import { App as AntdApp, Button, Collapse, Drawer, Form, Input, Modal, Popover, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { TablePaginationConfig } from 'antd'
import {
  DisconnectOutlined,
  EditOutlined,
  KeyOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  UnlockOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import ErrorResult from '@/components/feedback/ErrorResult'
import { useGroupBindingList, useZentaoMappingOptions } from '@/modules/groupBinding/queries'
import {
  useCreateUser,
  useResolveZentaoAccount,
  useResetUserPassword,
  useRevokeUserSessions,
  useSyncUserZentaoAccount,
  useUnlockUser,
  useUpdateUser,
  useUserList,
} from '@/modules/user/queries'
import type { AppUserItem, AppUserUpsertPayload, AppUserZentaoSyncResult } from '@/types/user'
import type { GroupBindingItem } from '@/types/groupBinding'
import { validatePasswordStrength } from '@/utils/passwordRules'

const { Text } = Typography

const ROLE_OPTIONS = [
  { value: 'service_agent', label: '接待' },
  { value: 'tech_owner', label: '技术' },
  { value: 'manager', label: '经理' },
  { value: 'admin', label: '管理员' },
  { value: 'auditor', label: '审计' },
]

const STATUS_OPTIONS = [
  { value: 'active', label: '启用' },
  { value: 'disabled', label: '停用' },
  { value: 'locked', label: '锁定' },
]

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  disabled: 'default',
  locked: 'red',
}

const STATUS_LABEL: Record<string, string> = {
  active: '启用',
  disabled: '停用',
  locked: '锁定',
}

const GROUP_SCOPE_TYPE_OPTIONS = [
  { value: 'service_owner', label: '主接待（可读写）' },
  { value: 'backup', label: '协助接待（可读写）' },
  { value: 'readonly', label: '只读查看' },
]

type UserFormValues = {
  account: string
  display_name: string
  email?: string
  status: string
  is_active: boolean
  roles: string[]
  group_scope_room_ids?: string[]
  group_scope_types?: Record<string, string>
  project_scope_values?: string[]
  zentao_account?: string
  zentao_name?: string
}

type PasswordResetFormValues = {
  temporary_password: string
  confirm_password: string
  must_change_password: boolean
}

function roleLabel(role: string) {
  return ROLE_OPTIONS.find((item) => item.value === role)?.label || role
}

function formatGroupScopeRoomIds(user: AppUserItem) {
  return user.group_scopes.map((item) => item.room_id)
}

function formatGroupScopeTypes(user: AppUserItem) {
  return Object.fromEntries(user.group_scopes.map((item) => [item.room_id, item.scope_type || 'service_owner']))
}

function groupScopeDisplayName(scope: AppUserItem['group_scopes'][number]) {
  return scope.room_name || scope.room_id
}

function formatProjectScopeValues(user: AppUserItem) {
  return user.project_scopes
    .map((item) => item.zentao_execution_id
      ? `execution:${item.zentao_project_id}:${item.zentao_execution_id}`
      : `project:${item.zentao_project_id}`)
}

function parseGroupScopes(roomIds?: string[], scopeTypes?: Record<string, string>) {
  return (roomIds || [])
    .map((roomId) => ({
      room_id: roomId,
      scope_type: scopeTypes?.[roomId] || 'service_owner',
    }))
    .filter((item) => item.room_id)
}

function parseProjectScopes(values?: string[]) {
  return (values || [])
    .flatMap((line) => {
      const [type, projectPart, executionPart] = line.split(':')
      const projectId = Number(projectPart)
      if (!Number.isFinite(projectId) || projectId <= 0) return []
      const executionId = type === 'execution' ? Number(executionPart) : null
      return [{
        zentao_project_id: projectId,
        zentao_execution_id: executionId !== null && Number.isFinite(executionId) && executionId > 0 ? executionId : undefined,
      }]
    })
}

function buildPayload(values: UserFormValues, includeAccount: boolean): AppUserUpsertPayload {
  const zentaoAccount = (values.zentao_account || '').trim()
  return {
    ...(includeAccount ? { account: values.account.trim() } : {}),
    display_name: values.display_name.trim(),
    email: (values.email || '').trim() || null,
    status: values.status,
    is_active: values.is_active,
    roles: values.roles || [],
    group_scopes: parseGroupScopes(values.group_scope_room_ids, values.group_scope_types),
    project_scopes: parseProjectScopes(values.project_scope_values),
    external_accounts: zentaoAccount
      ? [{ provider: 'zentao', external_account: zentaoAccount, external_name: (values.zentao_name || '').trim() || null }]
      : [],
  }
}

function groupBindingDisplayName(binding: GroupBindingItem) {
  return binding.room_name || binding.room_id
}

function projectScopeLabel(projectId: number, name?: string) {
  return `项目 #${projectId} ${name || ''}`.trim()
}

function executionScopeLabel(executionId: number, projectId: number, name?: string) {
  return `执行 #${executionId} ${name || ''} / 项目 #${projectId}`.trim()
}

function formatTime(value?: string | null) {
  return value?.replace('T', ' ').slice(0, 19) || '-'
}

export default function UserManagementPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<string | undefined>()
  const [groupKeyword, setGroupKeyword] = useState('')
  const [editing, setEditing] = useState<AppUserItem | null>(null)
  const [resetTarget, setResetTarget] = useState<AppUserItem | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm<UserFormValues>()
  const [resetForm] = Form.useForm<PasswordResetFormValues>()
  const { message, modal } = AntdApp.useApp()
  const selectedGroupRoomIds = Form.useWatch('group_scope_room_ids', form) || []

  const { data, isLoading, isError, error, refetch } = useUserList({
    page,
    page_size: pageSize,
    keyword: keyword || undefined,
    status,
  })
  const { data: groupData, isFetching: isGroupLoading } = useGroupBindingList({
    page: 1,
    page_size: 100,
    keyword: groupKeyword || undefined,
    is_active: true,
  })
  const { data: zentaoOptions, isFetching: isZentaoOptionsLoading } = useZentaoMappingOptions(undefined, drawerOpen)
  const createMutation = useCreateUser()
  const updateMutation = useUpdateUser()
  const resolveZentaoAccountMutation = useResolveZentaoAccount()
  const syncUserZentaoAccountMutation = useSyncUserZentaoAccount()
  const resetPasswordMutation = useResetUserPassword()
  const unlockUserMutation = useUnlockUser()
  const revokeSessionsMutation = useRevokeUserSessions()

  const groupBindings = groupData?.items || []
  const groupByRoomId = new Map(groupBindings.map((item) => [item.room_id, item]))
  const editingGroupScopeByRoomId = new Map((editing?.group_scopes || []).map((item) => [item.room_id, item]))
  const groupOptions = [
    ...selectedGroupRoomIds
      .filter((roomId) => !groupByRoomId.has(roomId))
      .map((roomId) => ({ value: roomId, label: groupScopeDisplayName(editingGroupScopeByRoomId.get(roomId) || { room_id: roomId, scope_type: 'service_owner' }) })),
    ...groupBindings.map((item) => ({
      value: item.room_id,
      label: groupBindingDisplayName(item),
    })),
  ]
  const selectedGroupRows = selectedGroupRoomIds.map((roomId) => {
    const binding = groupByRoomId.get(roomId)
    const scope = editingGroupScopeByRoomId.get(roomId)
    return {
      room_id: roomId,
      label: binding ? groupBindingDisplayName(binding) : groupScopeDisplayName(scope || { room_id: roomId, scope_type: 'service_owner' }),
    }
  })
  const projectScopeOptions = [
    ...(zentaoOptions?.projects || []).map((project) => ({
      value: `project:${project.project_id}`,
      label: projectScopeLabel(project.project_id, project.name),
    })),
    ...(zentaoOptions?.executions || []).map((execution) => ({
      value: `execution:${execution.project_id}:${execution.execution_id}`,
      label: executionScopeLabel(execution.execution_id, execution.project_id, execution.name),
    })),
  ]

  useEffect(() => {
    if (!drawerOpen) return
    const zentao = editing?.external_accounts.find((item) => item.provider === 'zentao')
    form.setFieldsValue({
      account: editing?.account || '',
      display_name: editing?.display_name || '',
      email: editing?.email || '',
      status: editing?.status || 'active',
      is_active: editing?.is_active ?? true,
      roles: editing?.roles || ['service_agent'],
      group_scope_room_ids: editing ? formatGroupScopeRoomIds(editing) : [],
      group_scope_types: editing ? formatGroupScopeTypes(editing) : {},
      project_scope_values: editing ? formatProjectScopeValues(editing) : [],
      zentao_account: zentao?.external_account || '',
      zentao_name: zentao?.external_name || '',
    })
    if (!editing) setGroupKeyword('')
  }, [drawerOpen, editing, form])

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  const openCreate = () => {
    setEditing(null)
    setDrawerOpen(true)
  }

  const openEdit = (user: AppUserItem) => {
    setEditing(user)
    setDrawerOpen(true)
  }

  const openResetPassword = (user: AppUserItem) => {
    setResetTarget(user)
    resetForm.setFieldsValue({
      temporary_password: '',
      confirm_password: '',
      must_change_password: true,
    })
  }

  const confirmUnlockUser = (user: AppUserItem) => {
    modal.confirm({
      title: `解锁账号：${user.display_name || user.account}`,
      content: '将清空临时锁定时间和连续失败次数；如果账号状态为锁定，会恢复为启用。',
      okText: '确认解锁',
      cancelText: '取消',
      onOk: async () => {
        await unlockUserMutation.mutateAsync(user.id)
        message.success('账号已解锁')
      },
    })
  }

  const confirmRevokeSessions = (user: AppUserItem) => {
    modal.confirm({
      title: `吊销会话：${user.display_name || user.account}`,
      content: '目标用户的未过期登录会话会立即失效，需要重新登录。',
      okText: '确认吊销',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await revokeSessionsMutation.mutateAsync(user.id)
        message.success(`已吊销 ${result.revoked_count} 个会话`)
      },
    })
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setEditing(null)
    setGroupKeyword('')
    form.resetFields()
  }

  const closeResetPassword = () => {
    setResetTarget(null)
    resetForm.resetFields()
  }

  const handleGroupScopeChange = (roomIds: string[]) => {
    const currentTypes = form.getFieldValue('group_scope_types') || {}
    form.setFieldsValue({
      group_scope_room_ids: roomIds,
      group_scope_types: Object.fromEntries(
        roomIds.map((roomId) => [roomId, currentTypes[roomId] || 'service_owner']),
      ),
    })
  }

  const submit = async () => {
    try {
      const values = await form.validateFields()
      if (editing) {
        await updateMutation.mutateAsync({ userId: editing.id, payload: buildPayload(values, false) })
        message.success('用户已更新')
      } else {
        await createMutation.mutateAsync(buildPayload(values, true))
        message.success('用户已创建')
      }
      closeDrawer()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存用户失败')
    }
  }

  const showZentaoSyncResult = (result: AppUserZentaoSyncResult, applyToForm = false) => {
    const sync = result.sync
    if (applyToForm && sync.external_account) {
      form.setFieldsValue({
        zentao_account: sync.external_account,
        zentao_name: sync.external_name || '',
      })
    }
    if (sync.external_account) {
      message.success(`禅道账号已同步：${sync.external_account}`)
    } else {
      message.warning(sync.message || '未找到唯一匹配的禅道账号')
    }
  }

  const syncDrawerZentaoAccount = async () => {
    try {
      if (editing) {
        const result = await syncUserZentaoAccountMutation.mutateAsync(editing.id)
        showZentaoSyncResult(result, true)
        return
      }
      const displayName = String(form.getFieldValue('display_name') || '').trim()
      if (!displayName) {
        message.warning('请先填写姓名')
        return
      }
      const result = await resolveZentaoAccountMutation.mutateAsync(displayName)
      showZentaoSyncResult(result, true)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '同步禅道账号失败')
    }
  }

  const syncRowZentaoAccount = async (user: AppUserItem) => {
    try {
      const result = await syncUserZentaoAccountMutation.mutateAsync(user.id)
      showZentaoSyncResult(result)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '同步禅道账号失败')
    }
  }

  const submitResetPassword = async () => {
    if (!resetTarget) return
    const values = await resetForm.validateFields()
    await resetPasswordMutation.mutateAsync({
      userId: resetTarget.id,
      payload: {
        temporary_password: values.temporary_password,
        must_change_password: values.must_change_password,
      },
    })
    message.success('密码已重置')
    closeResetPassword()
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const nextPageSize = pagination.pageSize || pageSize
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize)
      setPage(1)
      return
    }
    if (pagination.current) setPage(pagination.current)
  }

  return (
    <div data-testid="user-management-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="搜索账号/姓名/邮箱"
          style={{ width: 260 }}
          value={keyword}
          onChange={(event) => { setKeyword(event.target.value); setPage(1) }}
        />
        <Select
          allowClear
          placeholder="状态"
          style={{ width: 140 }}
          value={status}
          options={STATUS_OPTIONS}
          onChange={(value) => { setStatus(value); setPage(1) }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>刷新</Button>
        <Button type="primary" icon={<UserAddOutlined />} onClick={openCreate}>新增用户</Button>
      </Space>

      <Table<AppUserItem>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `共 ${total} 条`,
        }}
        scroll={{ x: 1740 }}
        columns={[
          {
            title: '账号',
            dataIndex: 'account',
            width: 150,
            fixed: 'left',
            render: (value: string, row) => (
              <Space orientation="vertical" size={0}>
                <Text strong>{value}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{row.source}</Text>
              </Space>
            ),
          },
          { title: '姓名', dataIndex: 'display_name', width: 150 },
          { title: '邮箱', dataIndex: 'email', width: 190, render: (value: string | null) => value || '-' },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (value: string, row) => (
              <Tag color={row.is_active ? STATUS_COLOR[value] || 'blue' : 'default'}>
                {row.is_active ? STATUS_LABEL[value] || value : '未启用'}
              </Tag>
            ),
          },
          {
            title: '登录安全',
            width: 150,
            render: (_: unknown, row) => {
              const tags = [
                row.must_change_password ? <Tag key="must" color="orange">需改密</Tag> : null,
                row.locked_until ? <Tag key="locked" color="red">临时锁定</Tag> : null,
                row.failed_login_count ? <Tag key="failed">失败 {row.failed_login_count}</Tag> : null,
              ].filter(Boolean)
              return tags.length ? <Space wrap size={[4, 4]}>{tags}</Space> : <Text type="secondary">正常</Text>
            },
          },
          {
            title: '角色',
            dataIndex: 'roles',
            width: 240,
            render: (roles: string[]) => (
              <Space wrap size={[4, 4]}>
                {roles.map((role) => <Tag key={role}>{roleLabel(role)}</Tag>)}
              </Space>
            ),
          },
          {
            title: '群范围',
            dataIndex: 'group_scopes',
            width: 220,
            render: (items: AppUserItem['group_scopes']) => {
              if (!items.length) return '-'
              const content = (
                <Space direction="vertical" size={4} style={{ maxWidth: 420 }}>
                  {items.map((item) => (
                    <Text key={item.room_id}>{groupScopeDisplayName(item)}</Text>
                  ))}
                </Space>
              )
              return (
                <Popover content={content} title="授权群聊" trigger="click">
                  <Button size="small">{items.length} 个群</Button>
                </Popover>
              )
            },
          },
          {
            title: '项目范围',
            dataIndex: 'project_scopes',
            width: 90,
            render: (items: AppUserItem['project_scopes']) => items.length || '-',
          },
          {
            title: '禅道账号',
            width: 150,
            render: (_: unknown, row) => row.external_accounts.find((item) => item.provider === 'zentao')?.external_account || '-',
          },
          {
            title: '更新时间',
            dataIndex: 'updated_at',
            width: 165,
            render: (value: string) => formatTime(value),
          },
          {
            title: '操作',
            width: 350,
            fixed: 'right',
            render: (_: unknown, row) => (
              <Space size={0} wrap>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>编辑</Button>
                <Button
                  type="link"
                  size="small"
                  icon={<SyncOutlined />}
                  loading={syncUserZentaoAccountMutation.isPending && syncUserZentaoAccountMutation.variables === row.id}
                  onClick={() => syncRowZentaoAccount(row)}
                >
                  同步禅道
                </Button>
                <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => openResetPassword(row)}>重置密码</Button>
                <Button
                  disabled={row.status !== 'locked' && !row.locked_until && !row.failed_login_count}
                  icon={<UnlockOutlined />}
                  size="small"
                  type="link"
                  onClick={() => confirmUnlockUser(row)}
                >
                  解锁
                </Button>
                <Button
                  danger
                  icon={<DisconnectOutlined />}
                  size="small"
                  type="link"
                  onClick={() => confirmRevokeSessions(row)}
                >
                  吊销会话
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={editing ? '编辑用户' : '新增用户'}
        open={drawerOpen}
        onClose={closeDrawer}
        size="large"
      >
        <div className="group-binding-drawer-actions">
          <Button
            block
            type="primary"
            loading={createMutation.isPending || updateMutation.isPending}
            onClick={submit}
          >
            保存
          </Button>
          <Button block onClick={closeDrawer}>取消</Button>
        </div>
        <Form form={form} layout="vertical">
          <Space style={{ width: '100%' }} size={12} align="start">
            <Form.Item name="account" label="账号" rules={[{ required: true, message: '请输入账号' }]}>
              <Input disabled={!!editing} style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="display_name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
              <Input style={{ width: 220 }} />
            </Form.Item>
          </Space>
          <Space style={{ width: '100%' }} size={12} align="start">
            <Form.Item name="email" label="邮箱">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
              <Select style={{ width: 160 }} options={STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item name="is_active" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="roles" label="角色" rules={[{ required: true, message: '请选择至少一个角色' }]}>
            <Select mode="multiple" options={ROLE_OPTIONS} placeholder="选择平台角色" />
          </Form.Item>
          <Form.Item
            name="group_scope_room_ids"
            label="群范围"
            extra={<Text type="secondary">接待和协助人员优先按群范围授权。可按群名、客户、项目或 room_id 搜索；留空表示仅按角色默认范围处理。</Text>}
          >
            <Select
              allowClear
              filterOption={false}
              loading={isGroupLoading}
              mode="multiple"
              optionFilterProp="label"
              options={groupOptions}
              placeholder="搜索并选择微信群"
              showSearch
              onChange={handleGroupScopeChange}
              onSearch={setGroupKeyword}
            />
          </Form.Item>
          {selectedGroupRows.length > 0 && (
            <Table
              size="small"
              rowKey="room_id"
              dataSource={selectedGroupRows}
              pagination={false}
              style={{ marginBottom: 16 }}
              columns={[
                {
                  title: '已选群',
                  dataIndex: 'label',
                  render: (value: string) => <Text>{value}</Text>,
                },
                {
                  title: '授权类型',
                  width: 190,
                  render: (_: unknown, row) => (
                    <Form.Item name={['group_scope_types', row.room_id]} noStyle>
                      <Select options={GROUP_SCOPE_TYPE_OPTIONS} />
                    </Form.Item>
                  ),
                },
              ]}
            />
          )}
          <Collapse
            size="small"
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'project-scopes',
                label: '高级授权：项目范围',
                children: (
                  <>
                    <Text type="secondary">
                      群和禅道项目一一绑定时，常规权限应只配置群范围；项目范围仅用于研发跨项目、临时协助或没有固定群绑定的兜底授权。
                    </Text>
                    <Form.Item
                      name="project_scope_values"
                      label="项目/执行范围"
                      style={{ marginTop: 12, marginBottom: 0 }}
                    >
                      <Select
                        allowClear
                        loading={isZentaoOptionsLoading}
                        mode="multiple"
                        optionFilterProp="label"
                        options={projectScopeOptions}
                        placeholder="高级场景才选择项目或执行"
                        showSearch
                      />
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
          <Space style={{ width: '100%' }} size={12} align="start">
            <Form.Item name="zentao_account" label="禅道账号">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="zentao_name" label="禅道姓名">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Button
              htmlType="button"
              icon={<SyncOutlined />}
              loading={resolveZentaoAccountMutation.isPending || syncUserZentaoAccountMutation.isPending}
              style={{ marginTop: 30 }}
              onClick={syncDrawerZentaoAccount}
            >
              同步禅道账号
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Modal
        title={`重置密码：${resetTarget?.display_name || resetTarget?.account || ''}`}
        open={!!resetTarget}
        okText="确认重置"
        cancelText="取消"
        onCancel={closeResetPassword}
        onOk={submitResetPassword}
        confirmLoading={resetPasswordMutation.isPending}
        destroyOnHidden
      >
        <Form
          form={resetForm}
          layout="vertical"
          initialValues={{ must_change_password: true }}
        >
          <Form.Item
            name="temporary_password"
            label="临时密码"
            rules={[
              { required: true, message: '请输入临时密码' },
              { validator: (_, value) => validatePasswordStrength(value) },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            dependencies={['temporary_password']}
            name="confirm_password"
            label="确认临时密码"
            rules={[
              { required: true, message: '请再次输入临时密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('temporary_password') === value) return Promise.resolve()
                  return Promise.reject(new Error('两次输入的临时密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="must_change_password"
            label="首次登录强制修改"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
